// Аналитический фильтр «⏷» по значениям столбца — общий для отчётов
// («Просроченная ДЗ», «Счета», «Потенциал», «Открытая прибыль»).
// Логика без React и Chakra — её проверяют тесты.

// Отбор хранится как { ключ столбца: Set(текстов ячеек) }; столбца без
// отбора в объекте нет.

// Сколько значений рисовать в списке за раз: у денежных столбцов их тысячи,
// и столько чекбоксов подвешивают страницу. Остальное — через поиск.
export const VALUE_LIST_LIMIT = 300;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}(?:[T ].*)?$/;

// Текст ячейки по умолчанию — как его показывают таблицы отчётов.
export const defaultCellText = (row, key) => {
  const raw = row?.[key];
  if (raw == null) return "";
  if (typeof raw === "number") {
    return Number.isFinite(raw) ? raw.toLocaleString("ru-RU", { maximumFractionDigits: 2 }) : "";
  }
  if (typeof raw === "boolean") return raw ? "Да" : "Нет";
  if (typeof raw === "string" && ISO_DATE.test(raw)) {
    const [y, m, d] = raw.slice(0, 10).split("-");
    return `${d}.${m}.${y}`;
  }
  return String(raw).trim();
};

// Ключ сортировки значения в списке: число — числом, дата — ISO, иначе текст.
export const defaultSortKey = (row, key) => {
  const raw = row?.[key];
  if (raw == null || raw === "") return null;
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  if (typeof raw === "string" && ISO_DATE.test(raw)) return raw.slice(0, 10);
  return defaultCellText(row, key) || null;
};

export const compareValues = (a, b) => {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), "ru", { numeric: true });
};

// Только отборы по столбцам из ``keys``: скрытый столбец не должен
// незаметно сужать таблицу.
export const pickFilters = (filters, keys) => {
  const allowed = new Set(keys);
  const result = {};
  Object.entries(filters || {}).forEach(([key, selected]) => {
    if (allowed.has(key)) result[key] = selected;
  });
  return result;
};

// Строки, прошедшие все отборы; ``skipKey`` — отбор, который не учитывается
// (свой столбец не сужает собственный список значений).
export const rowsPassing = (rows, filters, textOf = defaultCellText, skipKey = null) => {
  const entries = Object.entries(filters || {}).filter(([key]) => key !== skipKey);
  if (!entries.length) return rows;
  return rows.filter((row) => entries.every(([key, selected]) => selected.has(textOf(row, key))));
};

// Значения столбца со счётчиками, упорядоченные по ``mode``:
// "alpha" — по значению, "desc"/"asc" — по числу строк.
export const valueCounts = (rows, key, mode = "alpha", textOf = defaultCellText, sortKeyOf = defaultSortKey) => {
  const counts = new Map();
  const order = new Map();
  rows.forEach((row) => {
    const name = textOf(row, key);
    counts.set(name, (counts.get(name) || 0) + 1);
    if (!order.has(name)) order.set(name, sortKeyOf(row, key));
  });
  const values = Array.from(counts, ([name, count]) => ({ name, count }));
  if (mode === "desc") values.sort((a, b) => b.count - a.count || compareValues(order.get(a.name), order.get(b.name)));
  else if (mode === "asc") values.sort((a, b) => a.count - b.count || compareValues(order.get(a.name), order.get(b.name)));
  else values.sort((a, b) => compareValues(order.get(a.name), order.get(b.name)));
  return values;
};

// Новый отбор столбца: выбраны все значения — отбора нет.
export const applySelection = (filters, key, allNames, selected) => {
  const rest = { ...filters };
  if (allNames.every((name) => selected.has(name))) delete rest[key];
  else rest[key] = selected;
  return rest;
};
